import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  DragIndicator,
  NavigateBefore,
  NavigateNext,
  Close
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  Section, 
  SectionType, 
  SECTION_TYPES, 
  generateSectionId,
  FestojoFormData
} from '../../types/festejo';
import { StepComponentProps } from './FestojoFormContainer';
import TextoSectionEditor from './TextoSectionEditor';
import ImagemSectionEditor from './ImagemSectionEditor';
import ProgramacaoSectionEditor from './ProgramacaoSectionEditor';

interface SectionsFormProps extends StepComponentProps {}

interface SectionEditorProps {
  section: Section | null;
  onSave: (section: Section) => void;
  onCancel: () => void;
}

const SectionsForm: React.FC<SectionsFormProps> = ({
  formData,
  onUpdate,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep
}) => {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedSectionType, setSelectedSectionType] = useState<SectionType | null>(null);
  const [editingSection, setEditingSection] = useState<Section | null>(null);

  // Abrir dialog para adicionar seção
  const handleAddSection = (type: SectionType) => {
    setSelectedSectionType(type);
    setEditingSection(null);
    setDialogOpen(true);
  };

  // Abrir dialog para editar seção
  const handleEditSection = (section: Section) => {
    setSelectedSectionType(section.type);
    setEditingSection(section);
    setDialogOpen(true);
  };

  // Fechar dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSectionType(null);
    setEditingSection(null);
  };

  // Salvar seção
  const handleSaveSection = (section: Section) => {
    const sections = [...formData.sections];
    
    if (editingSection) {
      // Editando seção existente
      const index = sections.findIndex(s => s.id === editingSection.id);
      if (index !== -1) {
        sections[index] = section;
      }
    } else {
      // Adicionando nova seção
      const newSection: Section = {
        ...section,
        id: generateSectionId(),
        order: sections.length
      };
      sections.push(newSection);
    }

    onUpdate({ sections });
    handleCloseDialog();
  };

  // Excluir seção
  const handleDeleteSection = (sectionId: string) => {
    const sections = formData.sections.filter(s => s.id !== sectionId);
    // Reordenar
    const reorderedSections = sections.map((section, index) => ({
      ...section,
      order: index
    }));
    onUpdate({ sections: reorderedSections });
  };

  // Reordenar seções (drag and drop)
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sections = Array.from(formData.sections);
    const [reorderedSection] = sections.splice(result.source.index, 1);
    sections.splice(result.destination.index, 0, reorderedSection);

    // Atualizar ordem
    const reorderedSections = sections.map((section, index) => ({
      ...section,
      order: index
    }));

    onUpdate({ sections: reorderedSections });
  };

  // Renderizar editor de seção baseado no tipo
  const renderSectionEditor = (): React.ReactNode => {
    if (!selectedSectionType) return null;

    const commonProps = {
      section: editingSection,
      onSave: handleSaveSection,
      onCancel: handleCloseDialog
    };

    switch (selectedSectionType) {
      case 'texto':
        return <TextoSectionEditor {...commonProps} />;
      case 'imagem':
        return <ImagemSectionEditor {...commonProps} />;
      case 'programacao':
        return <ProgramacaoSectionEditor {...commonProps} />;
      // Adicionar outros editores conforme necessário
      default:
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Editor para {SECTION_TYPES[selectedSectionType].label} em desenvolvimento</Typography>
            <Button onClick={handleCloseDialog} sx={{ mt: 2 }}>
              Fechar
            </Button>
          </Box>
        );
    }
  };

  // Renderizar item da seção na lista
  const renderSectionItem = (section: Section, index: number) => {
    const sectionType = SECTION_TYPES[section.type];
    
    return (
      <Draggable key={section.id} draggableId={section.id} index={index}>
        {(provided, snapshot) => (
          <ListItem
            ref={provided.innerRef}
            {...provided.draggableProps}
            sx={{
              mb: 1,
              backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'white',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: '#f9f9f9'
              }
            }}
          >
            <Box
              {...provided.dragHandleProps}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mr: 2,
                color: 'text.secondary',
                cursor: 'grab'
              }}
            >
              <DragIndicator />
            </Box>

            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{sectionType.icon}</span>
                  <Typography variant="subtitle1">
                    {section.title}
                  </Typography>
                  <Chip 
                    label={sectionType.label} 
                    size="small" 
                    variant="outlined"
                  />
                </Box>
              }
              secondary={sectionType.description}
            />

            <ListItemSecondaryAction>
              <IconButton 
                onClick={() => handleEditSection(section)}
                size="small"
                sx={{ mr: 1 }}
              >
                <Edit />
              </IconButton>
              <IconButton 
                onClick={() => handleDeleteSection(section.id)}
                size="small"
                color="error"
              >
                <Delete />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        )}
      </Draggable>
    );
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Seções do Festejo
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Adicione e organize as seções que aparecerão no site do festejo
      </Typography>

      <Grid container spacing={3}>
        {/* Lista de seções existentes */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Seções Criadas ({formData.sections.length})
            </Typography>

            {formData.sections.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Nenhuma seção criada ainda. Adicione seções usando os botões ao lado.
              </Alert>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="sections">
                  {(provided) => (
                    <List
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{ minHeight: 100 }}
                    >
                      {formData.sections
                        .sort((a, b) => a.order - b.order)
                        .map((section, index) => renderSectionItem(section, index))}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Paper>
        </Grid>

        {/* Tipos de seções disponíveis */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Adicionar Seção
            </Typography>

            <Grid container spacing={2}>
              {Object.entries(SECTION_TYPES).map(([type, config]) => (
                <Grid item xs={12} key={type}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                    onClick={() => handleAddSection(type as SectionType)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <span style={{ fontSize: '1.2em' }}>{config.icon}</span>
                        <Typography variant="subtitle2">
                          {config.label}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {config.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Dicas */}
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              💡 Dicas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Arraste as seções para reordená-las<br/>
              • Use seções de texto para história e tradições<br/>
              • Adicione uma programação com os eventos<br/>
              • Inclua uma galeria com fotos anteriores<br/>
              • Links externos podem levar para mapas ou formulários
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog para editar seção */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedSectionType && (
              <>
                <span>{SECTION_TYPES[selectedSectionType].icon}</span>
                <Typography variant="h6">
                  {editingSection ? 'Editar' : 'Adicionar'} {SECTION_TYPES[selectedSectionType].label}
                </Typography>
              </>
            )}
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {renderSectionEditor()}
        </DialogContent>
      </Dialog>

      {/* Botões de navegação */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onPrevious}
          startIcon={<NavigateBefore />}
          size="large"
        >
          Anterior
        </Button>

        <Button
          variant="contained"
          onClick={onNext}
          endIcon={<NavigateNext />}
          size="large"
        >
          Próximo
        </Button>
      </Box>
    </Box>
  );
};

export default SectionsForm;

